/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { getCustomerAmountFieldError, getCustomerChargeBlockedError, getCustomerPaymentAmountError, formatCustomerSelectedSaleLabel, getCustomerMixedDebtBreakdown, getCustomerMixedDebtPaymentHint, getCustomerPaymentNoDebtHint, isCustomerAmountReady } from '../application/validateCustomerManualMovement';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { validateCustomerCheckDrafts } from '../application/validateCustomerCheckDrafts';
import type { CustomerCheckDraft } from '../store/useCustomerOverlayStore';
import { formatLocalDate } from '@shared/vueltoDateTime';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker'
import type { ModalPickerOption } from '../../shared/modals/ModalNestedPicker'
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal'
import type { OperationalConfirmModalProps } from '../../shared/modals/OperationalConfirmModal'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { openSaleDetail } from '../../sales/application/salesNavigation';
import { preserveCustomerDetailForShellNavigation } from '../screens/customerDetailPreserve';
import type { CustomerAccountRow, CustomerMovementRow } from '../domain/customerTypes';
import type { CustomerPaymentMethodId } from '../infra/customerPayments';
import type { PickedAttachmentFile } from '../../shared/attachments/infra/pickAttachmentFile';
import { getCustomerMovementDisplayLabel, getCustomerPaymentMethodLabel } from '../domain/customerMovementLabels';
import { customerMovementModalStyles as styles } from './CustomerMovementModal.styles';
import { formatMoney } from '../../shared/format/public-api';

type MovementMode = 'payment' | 'charge';

const PAYMENT_METHOD_OPTIONS: Array<{ value: CustomerPaymentMethodId; label: string }> = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'mercadopago_qr', label: 'Mercado Pago QR' },
  { value: 'cheque', label: 'Cheque' },
];

const MOVEMENT_MODE_OPTIONS: Array<{ value: MovementMode; label: string; shortLabel: string }> = [
  { value: 'payment', label: 'Registrar cobro', shortLabel: 'Cobro' },
  { value: 'charge', label: 'Consumo manual', shortLabel: 'Consumo' },
];

export type CustomerSaleLinkOption = {
  id: string;
  shortId: string;
  createdAt: string;
  total: number;
  paidAmount: number;
  pendingAmount: number;
};

export type CustomerMovementModalProps = {
  visible: boolean;
  onClose: () => void;
  customer: CustomerAccountRow | null;
  movements: CustomerMovementRow[];
  loading: boolean;
  movementsHasMore: boolean;
  movementsLoadingMore: boolean;
  onLoadMore: () => void;
  amount: string;
  note: string;
  setAmount: (value: string) => void;
  setNote: (value: string) => void;
  onCharge: () => void;
  onPayment: () => void;
  saving: boolean;
  canCollect: boolean;
  canAdjustDebt: boolean;
  previewMode?: boolean;
  onOpenFullHistory?: () => void;
  selectedSaleId: string | null;
  setSelectedSaleId: (saleId: string | null) => void;
  customerSalesOptions: CustomerSaleLinkOption[];
  loadingCustomerSales: boolean;
  /** Medio de cobro elegido (solo modo Cobro). */
  paymentMethod: CustomerPaymentMethodId | null;
  setPaymentMethod: (method: CustomerPaymentMethodId | null) => void;
  /** Drafts de cheques (multi-cheque Fase C). */
  checkDrafts: CustomerCheckDraft[];
  updateCheckDraft: (index: number, patch: Partial<CustomerCheckDraft>) => void;
  addCheckDraft: () => void;
  removeCheckDraft: (index: number) => void;
  /** Capability `checks` — muestra Cheque como medio. */
  checksEnabled: boolean;
  /** MP QR disponible (flag + entitlement + POS MP configurado). */
  mercadopagoQrEnabled: boolean;
  /** Sin caja abierta (módulo caja activo): bloquea TODOS los medios de cobro —
   *  un cobro sin sesión queda con cash_session_id null e invisible en arqueo. */
  cashLocked: boolean;
  /** Comprobantes elegidos que se adjuntan al registrar el cobro. */
  pendingAttachments: PickedAttachmentFile[];
  attachmentBusy: boolean;
  onAddAttachment: () => void;
  onRemoveAttachment: (index: number) => void;
  /** Abre el detalle del movimiento (comprobantes, datos del cheque). */
  onOpenMovementDetail: (movement: CustomerMovementRow) => void;
  confirmOverlay?: Omit<OperationalConfirmModalProps, 'visible' | 'presentation'> | null;
};

export function CustomerMovementModal({
  visible,
  onClose,
  customer,
  movements,
  loading,
  movementsLoadingMore,
  onLoadMore,
  amount,
  note,
  setAmount,
  setNote,
  onCharge,
  onPayment,
  saving,
  canCollect,
  canAdjustDebt,
  previewMode,
  onOpenFullHistory,
  selectedSaleId,
  setSelectedSaleId,
  customerSalesOptions,
  loadingCustomerSales,
  paymentMethod,
  setPaymentMethod,
  checkDrafts,
  updateCheckDraft,
  addCheckDraft,
  removeCheckDraft,
  checksEnabled,
  mercadopagoQrEnabled,
  cashLocked,
  pendingAttachments,
  attachmentBusy,
  onAddAttachment,
  onRemoveAttachment,
  onOpenMovementDetail,
  confirmOverlay,
}: CustomerMovementModalProps) {
  const navigation = useNavigation();
  const { width, height: winH } = useWindowDimensions();
  const isDesktop = width >= 768;
  const [movementMode, setMovementMode] = useState<MovementMode>('payment');
  const [salePickerVisible, setSalePickerVisible] = useState(false);
  const [methodPickerVisible, setMethodPickerVisible] = useState(false);
  const [discardConfirmVisible, setDiscardConfirmVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setMovementMode('payment');
      setDiscardConfirmVisible(false);
    }
  }, [visible]);

  // Dirty-guard: cerrar con datos cargados (monto, nota, cheques, adjuntos)
  // pide confirmación para no perderlos. El medio de cobro es un default
  // (efectivo/transferencia según caja) — elegirlo no es "datos cargados".
  const isDirty =
    amount.trim().length > 0 ||
    note.trim().length > 0 ||
    pendingAttachments.length > 0 ||
    checkDrafts.some(
      (d) =>
        d.number.trim().length > 0 ||
        d.dueDate.trim().length > 0 ||
        d.drawer.trim().length > 0 ||
        d.bank.trim().length > 0 ||
        d.amount.trim().length > 0
    );

  const handleClose = () => {
    if (isDirty && !saving) {
      setDiscardConfirmVisible(true);
      return;
    }
    onClose();
  };

  const handleMovementModeChange = (mode: MovementMode) => {
    setMovementMode(mode);
    if (mode === 'charge') {
      setSelectedSaleId(null);
    }
  };

  const methodOptions = useMemo(
    () =>
      cashLocked
        ? []
        : PAYMENT_METHOD_OPTIONS.filter(
            (opt) =>
              (opt.value !== 'cheque' || checksEnabled) &&
              (opt.value !== 'mercadopago_qr' || mercadopagoQrEnabled)
          ),
    [checksEnabled, cashLocked, mercadopagoQrEnabled]
  );

  const methodPickerOptions = useMemo(
    (): ModalPickerOption[] => methodOptions.map((opt) => ({ value: opt.value, label: opt.label })),
    [methodOptions]
  );

  const paymentMethodLabel = useMemo(() => {
    const found = methodOptions.find((opt) => opt.value === paymentMethod);
    return found?.label ?? 'Elegí un medio de cobro';
  }, [methodOptions, paymentMethod]);

  const salePickerOptions = useMemo((): ModalPickerOption[] => {
    const fifoOption: ModalPickerOption = {
      value: '',
      label: 'Automático: primero las más viejas',
    };
    const saleOptions = customerSalesOptions.map((sale) => ({
      value: sale.id,
      label: `${formatLocalDate(sale.createdAt)} · Venta #${sale.shortId} · ${formatMoney(sale.total)} (pendiente ${formatMoney(sale.pendingAmount)})`,
    }));
    return [fifoOption, ...saleOptions];
  }, [customerSalesOptions]);

  const selectedSale = useMemo(
    () => (selectedSaleId ? customerSalesOptions.find((s) => s.id === selectedSaleId) ?? null : null),
    [customerSalesOptions, selectedSaleId]
  );

  const saleLinkLabel = selectedSale
    ? formatCustomerSelectedSaleLabel(selectedSale)
    : 'Automático: primero las más viejas';

  const hasPendingCustomerSales = customerSalesOptions.length > 0;

  const balance = customer?.balance ?? 0;
  const amountReady = isCustomerAmountReady(amount);
  const amountError = useMemo(
    () => (amount.trim().length > 0 ? getCustomerAmountFieldError(amount) : null),
    [amount]
  );
  const paymentAmountError = useMemo(() => {
    if (movementMode !== 'payment') return null;
    if (amount.trim().length === 0) return null;
    // When a sale is linked via collectIntent but hasn't appeared in the list
    // yet (sync timing), skip amount validation — the intent already provides
    // the correct amount.
    if (selectedSaleId && !selectedSale) return null;
    return getCustomerPaymentAmountError(amount, balance, selectedSale);
  }, [movementMode, amount, balance, selectedSale, selectedSaleId]);
  const paymentFieldError = amountError ?? paymentAmountError;
  const chargeBlocked = customer ? getCustomerChargeBlockedError(customer) : null;
  // "Sin deuda" hint only applies when no specific sale is selected — collecting
  // a pending sale is valid even if the customer's current balance is $0 (the
  // sale's pending amount is what matters in that case).
  // When a saleId is linked via collectIntent, trust it even if the sale hasn't
  // loaded into customerSalesOptions yet (sync timing from Ventas → Clientes).
  const paymentNoDebt =
    customer && !selectedSaleId && !selectedSale
      ? getCustomerPaymentNoDebtHint(balance)
      : null;
  const canRegisterCharge = canAdjustDebt && amountReady && !chargeBlocked;
  const checkDraftsSum = useMemo(
    () =>
      checkDrafts.reduce(
        (acc, d) => acc + (parseDecimalInput(d.amount, { min: 0 }) ?? 0),
        0
      ),
    [checkDrafts]
  );
  const paymentValue = useMemo(
    () => parseDecimalInput(amount, { allowNegative: true }) ?? 0,
    [amount]
  );
  const checkReady =
    paymentMethod !== 'cheque' || validateCustomerCheckDrafts(checkDrafts, paymentValue).ok;
  const canRegisterPayment =
    canCollect && amountReady && !paymentNoDebt && !paymentAmountError && !!paymentMethod && checkReady && !cashLocked;

  const mixedDebtHint = useMemo(() => {
    if (loadingCustomerSales || balance <= 0) return null;
    const breakdown = getCustomerMixedDebtBreakdown(balance, customerSalesOptions);
    return getCustomerMixedDebtPaymentHint(breakdown);
  }, [balance, customerSalesOptions, loadingCustomerSales]);

  const headerSubtitle = customer
    ? `${customer.name} · Saldo ${formatMoney(balance)}`
    : 'Cliente';

  return (
    <Modal
      visible={visible}
      animationType={isDesktop ? 'fade' : 'slide'}
      transparent
      onRequestClose={handleClose}
    >
      <View
        style={[
          styles.fullBackdrop,
          isDesktop && { justifyContent: 'center', alignItems: 'center' },
        ]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
        <View
          style={[
            styles.fullCard,
            { maxHeight: winH * 0.96 },
            isDesktop && {
              maxWidth: 640,
              alignSelf: 'center',
              borderRadius: 16,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
          ]}
        >
          {!isDesktop ? <View style={styles.grabber} /> : null}
          <TicketModalHeader
            kicker="Clientes"
            title="Cobros y pagos"
            subtitle={headerSubtitle}
            onClose={handleClose}
            compact={!isDesktop}
          />
          <ModalAwareScrollView
            style={styles.contentScroll}
            contentContainerStyle={styles.contentScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            bottomOffset={spacing.sm}
            nestedScrollEnabled
          >
          <View collapsable={false}>
          {customer?.blocked ? (
            <View style={modalExperience.warningPanel}>
              <Text style={posUi.checkoutHint}>
                Cliente bloqueado: solo podés registrar pagos, no nuevos consumos.
              </Text>
            </View>
          ) : null}
          {canCollect || canAdjustDebt ? (
            <View style={styles.formBody}>
              <View style={styles.modeSwitch} accessibilityRole="tablist">
                {MOVEMENT_MODE_OPTIONS.map((option, index) => {
                  const active = movementMode === option.value;
                  const isLast = index === MOVEMENT_MODE_OPTIONS.length - 1;
                  const chargeDisabled =
                    option.value === 'charge' && (!canAdjustDebt || !!customer?.blocked);
                  const paymentDisabled =
                    option.value === 'payment' && !canCollect;
                  const disabled = chargeDisabled || paymentDisabled;
                  return (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.modeSegment,
                        active && styles.modeSegmentActive,
                        isLast && styles.modeSegmentLast,
                        disabled && styles.modeSegmentDisabled,
                      ]}
                      onPress={() => {
                        if (disabled) return;
                        handleMovementModeChange(option.value);
                      }}
                      disabled={disabled}
                      accessibilityRole="tab"
                      accessibilityLabel={option.label}
                      accessibilityState={{ selected: active, disabled }}
                    >
                      <Text
                        style={[
                          styles.modeSegmentText,
                          active && styles.modeSegmentTextActive,
                          chargeDisabled && styles.modeSegmentTextDisabled,
                        ]}
                        numberOfLines={1}
                      >
                        {option.shortLabel}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {movementMode === 'payment' ? (
                <View style={styles.fieldRow}>
                  <ModalFormField label="Monto" required style={styles.fieldCol}>
                    <NumericTextInput
                      kind="decimalMoney"
                      value={amount}
                      onChangeText={setAmount}
                      placeholder="Ej. 1500"
                      style={[
                        modalExperience.formInput,
                        paymentFieldError && styles.inputInvalid,
                      ]}
                      placeholderTextColor={colors.inputPlaceholder}
                    />
                    {paymentFieldError ? (
                      <Text style={styles.fieldError}>{paymentFieldError.message}</Text>
                    ) : null}
                  </ModalFormField>
                  <ModalFormField label="Medio de cobro" required style={styles.fieldCol}>
                    <TouchableOpacity
                      style={[
                        modalExperience.entitySelector,
                        paymentMethod && modalExperience.entitySelectorActive,
                      ]}
                      onPress={() => setMethodPickerVisible(true)}
                      accessibilityRole="button"
                      accessibilityLabel="Elegir medio de cobro"
                      testID="customers.movement.payment-method"
                    >
                      <Text style={modalExperience.filterSelectValue} numberOfLines={1}>
                        {paymentMethodLabel}
                      </Text>
                      <Ionicons name="chevron-down" size={18} color={ticketColors.ink2} />
                    </TouchableOpacity>
                    {cashLocked ? (
                      <Text style={styles.actionHint}>Abrí caja para registrar cobros.</Text>
                    ) : null}
                  </ModalFormField>
                </View>
              ) : (
                <ModalFormField label="Monto" required>
                  <NumericTextInput
                    kind="decimalMoney"
                    value={amount}
                    onChangeText={setAmount}
                    placeholder="Ej. 1500"
                    style={[
                      modalExperience.formInput,
                      paymentFieldError && styles.inputInvalid,
                    ]}
                    placeholderTextColor={colors.inputPlaceholder}
                  />
                  {paymentFieldError ? (
                    <Text style={styles.fieldError}>{paymentFieldError.message}</Text>
                  ) : null}
                </ModalFormField>
              )}

              <ModalFormField label="Nota">
                <TextInput
                  value={note}
                  onChangeText={setNote}
                  placeholder="Ej. pago parcial, entrega de cheque"
                  style={modalExperience.formInput}
                  placeholderTextColor={colors.inputPlaceholder}
                />
              </ModalFormField>

              {movementMode === 'payment' ? (
                <>
                  {paymentMethod === 'cheque' ? (
                    <ModalFormField
                      label={checkDrafts.length > 1 ? `Datos de los ${checkDrafts.length} cheques` : 'Datos del cheque'}
                    >
                      {checkDrafts.map((draft, index) => (
                        <View key={index} style={styles.checkDraftCard}>
                          {checkDrafts.length > 1 ? (
                            <View style={styles.checkDraftHeader}>
                              <Text style={styles.checkDraftTitle}>Cheque {index + 1}</Text>
                              <TouchableOpacity
                                onPress={() => removeCheckDraft(index)}
                                accessibilityRole="button"
                                accessibilityLabel={`Quitar cheque ${index + 1}`}
                                testID={`customers.movement.check-remove-${index}`}
                                hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
                              >
                                <Ionicons name="trash-outline" size={16} color={ticketColors.bad} />
                              </TouchableOpacity>
                            </View>
                          ) : null}
                          <View style={styles.inputRow}>
                            <ModalFormField label="Número" required style={styles.inputAmount}>
                              <TextInput
                                value={draft.number}
                                onChangeText={(v) => updateCheckDraft(index, { number: v })}
                                placeholder="Ej. 00123456"
                                style={modalExperience.formInput}
                                placeholderTextColor={colors.inputPlaceholder}
                                testID={
                                  index === 0
                                    ? 'customers.movement.check-number'
                                    : `customers.movement.check-number-${index}`
                                }
                              />
                            </ModalFormField>
                            <ModalFormField label="Vencimiento" style={styles.inputNote}>
                              <TextInput
                                value={draft.dueDate}
                                onChangeText={(v) => updateCheckDraft(index, { dueDate: v })}
                                placeholder="AAAA-MM-DD"
                                style={modalExperience.formInput}
                                placeholderTextColor={colors.inputPlaceholder}
                                testID={
                                  index === 0
                                    ? 'customers.movement.check-due-date'
                                    : `customers.movement.check-due-date-${index}`
                                }
                              />
                            </ModalFormField>
                          </View>
                          <View style={styles.inputRow}>
                            <TextInput
                              value={draft.drawer}
                              onChangeText={(v) => updateCheckDraft(index, { drawer: v })}
                              placeholder="Librador"
                              style={[modalExperience.formInput, styles.inputAmount]}
                              placeholderTextColor={colors.inputPlaceholder}
                            />
                            <TextInput
                              value={draft.bank}
                              onChangeText={(v) => updateCheckDraft(index, { bank: v })}
                              placeholder="Banco"
                              style={[modalExperience.formInput, styles.inputNote]}
                              placeholderTextColor={colors.inputPlaceholder}
                            />
                          </View>
                          {checkDrafts.length > 1 ? (
                            <View style={styles.inputRow}>
                              <TextInput
                                value={draft.amount}
                                onChangeText={(v) => updateCheckDraft(index, { amount: v })}
                                placeholder="Monto del cheque"
                                style={[modalExperience.formInput, styles.inputAmount]}
                                placeholderTextColor={colors.inputPlaceholder}
                                keyboardType="decimal-pad"
                                testID={`customers.movement.check-amount-${index}`}
                              />
                              <View style={styles.inputNote} />
                            </View>
                          ) : null}
                        </View>
                      ))}
                      <TouchableOpacity
                        onPress={addCheckDraft}
                        accessibilityRole="button"
                        accessibilityLabel="Agregar otro cheque"
                        style={styles.addCheckBtn}
                        testID="customers.movement.add-check"
                      >
                        <Ionicons name="add-circle-outline" size={18} color={ticketColors.gold} />
                        <Text style={styles.addCheckBtnText}>Agregar otro cheque</Text>
                      </TouchableOpacity>
                      {checkDrafts.length > 1 ? (
                        <Text
                          style={[
                            styles.actionHint,
                            Math.abs(checkDraftsSum - paymentValue) > 0.009 && styles.checkSumError,
                          ]}
                          testID="customers.movement.check-sum-hint"
                        >
                          Suma de cheques: {formatMoney(checkDraftsSum)} — tiene que ser {formatMoney(paymentValue)}
                        </Text>
                      ) : null}
                    </ModalFormField>
                  ) : null}
                  {paymentMethod !== 'mercadopago_qr' ? (
                  <ModalFormField label="Comprobante">
                    <TouchableOpacity
                      style={[
                        modalExperience.entitySelector,
                        pendingAttachments.length > 0 && modalExperience.entitySelectorActive,
                        attachmentBusy && styles.modeSegmentDisabled,
                      ]}
                      onPress={onAddAttachment}
                      disabled={attachmentBusy}
                      accessibilityRole="button"
                      accessibilityLabel="Adjuntar comprobante"
                      testID="customers.movement.add-attachment"
                    >
                      <Ionicons name="attach" size={18} color={ticketColors.ink2} />
                      <Text style={modalExperience.filterSelectValue} numberOfLines={1}>
                        {attachmentBusy
                          ? 'Leyendo archivo…'
                          : pendingAttachments.length === 0
                            ? 'Adjuntar foto o PDF (opcional)'
                            : `${pendingAttachments.length} archivo${pendingAttachments.length === 1 ? '' : 's'} elegido${pendingAttachments.length === 1 ? '' : 's'}`}
                      </Text>
                    </TouchableOpacity>
                    {pendingAttachments.map((file, index) => (
                      <View key={`${file.fileName}-${index}`} style={styles.attachmentChipRow}>
                        <Ionicons
                          name={file.mimeType === 'application/pdf' ? 'document-text-outline' : 'image-outline'}
                          size={16}
                          color={ticketColors.ink2}
                        />
                        <Text style={styles.attachmentChipName} numberOfLines={1}>
                          {file.fileName}
                        </Text>
                        <TouchableOpacity
                          onPress={() => onRemoveAttachment(index)}
                          accessibilityRole="button"
                          accessibilityLabel={`Quitar ${file.fileName}`}
                          style={styles.attachmentChipRemove}
                        >
                          <Ionicons name="close-circle" size={18} color={ticketColors.ink2} />
                        </TouchableOpacity>
                      </View>
                    ))}
                    {paymentMethod === 'cheque' ? (
                      <Text style={styles.actionHint}>Podés adjuntar la foto del cheque (frente y dorso).</Text>
                    ) : paymentMethod === 'transfer' ? (
                      <Text style={styles.actionHint}>Podés adjuntar el comprobante de la transferencia.</Text>
                    ) : null}
                  </ModalFormField>
                  ) : null}
                  <ModalFormField label="Asignar a una venta">
                    {loadingCustomerSales ? (
                      <ActivityIndicator color={ticketColors.gold} size="small" style={styles.saleLinkLoader} />
                    ) : hasPendingCustomerSales ? (
                      <View style={styles.saleLinkRow}>
                        <TouchableOpacity
                          style={[
                            modalExperience.entitySelector,
                            styles.saleLinkSelector,
                            selectedSaleId && modalExperience.entitySelectorActive,
                          ]}
                          onPress={() => setSalePickerVisible(true)}
                          accessibilityRole="button"
                          accessibilityLabel="Elegir venta o bajar el saldo"
                        >
                          <Text style={modalExperience.filterSelectValue} numberOfLines={2}>
                            {saleLinkLabel}
                          </Text>
                          <Ionicons name="chevron-down" size={18} color={ticketColors.ink2} />
                        </TouchableOpacity>
                        {selectedSaleId ? (
                          <TouchableOpacity
                            onPress={() => setSelectedSaleId(null)}
                            style={styles.saleLinkClear}
                            accessibilityRole="button"
                            accessibilityLabel="Sin venta: baja el saldo"
                          >
                            <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    ) : (
                      <View style={[modalExperience.entitySelector, styles.saleLinkStatic]}>
                        <Text style={modalExperience.filterSelectValue}>{saleLinkLabel}</Text>
                      </View>
                    )}
                  </ModalFormField>
                  {mixedDebtHint && !selectedSaleId ? (
                    <View style={[modalExperience.infoPanel, styles.mixedDebtHintPanel]}>
                      <View style={styles.mixedDebtHintHeader}>
                        <Ionicons name="alert-circle-outline" size={18} color={ticketColors.gold} />
                        <Text style={styles.mixedDebtHintTitle}>{mixedDebtHint.title}</Text>
                      </View>
                      {mixedDebtHint.lines.map((line) => (
                        <Text key={line} style={[typography.caption, styles.mixedDebtHintLine]}>
                          {line}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                  {selectedSale ? (
                    <View style={styles.saleLinkInfoRow}>
                      <Ionicons name="information-circle" size={16} color={ticketColors.gold} />
                      <Text style={[typography.caption, styles.saleLinkInfoText]}>
                        {`El cobro va solo a esta venta (máximo ${formatMoney(selectedSale.pendingAmount)}).`}
                      </Text>
                    </View>
                  ) : null}
                  {!selectedSaleId && hasPendingCustomerSales && !mixedDebtHint ? (
                    <View style={styles.saleLinkInfoRow}>
                      <Ionicons name="information-circle" size={16} color={ticketColors.ink2} />
                      <Text style={[typography.caption, styles.saleLinkInfoText]}>
                        Sin venta elegida, el cobro baja las ventas pendientes, de la más vieja a la más nueva. Elegí una venta arriba para cobrar esa.
                      </Text>
                    </View>
                  ) : null}
                  {!hasPendingCustomerSales && !loadingCustomerSales ? (
                    <View style={styles.saleLinkInfoRow}>
                      <Ionicons name="information-circle" size={16} color={ticketColors.ink2} />
                      <Text style={[typography.caption, styles.saleLinkInfoText]}>
                        No hay ventas pendientes. El cobro baja el saldo del cliente.
                      </Text>
                    </View>
                  ) : null}
                  <TouchableOpacity
                    style={[
                      posUi.primaryButton,
                      posUi.modalActionFullWidth,
                      (saving || !canRegisterPayment) && posUi.disabledCard,
                    ]}
                    onPress={onPayment}
                    disabled={saving || !canRegisterPayment}
                    accessibilityRole="button"
                    accessibilityLabel="Registrar cobro"
                    testID="customers.movement.register-payment"
                  >
                    <Text style={posUi.primaryButtonText}>{saving ? 'Guardando…' : 'Registrar cobro'}</Text>
                  </TouchableOpacity>
                  {paymentNoDebt && amountReady ? (
                    <Text style={styles.actionHint}>{paymentNoDebt.message}</Text>
                  ) : null}
                </>
              ) : (
                <>
                  <View style={modalExperience.infoPanel}>
                    <View style={styles.chargeInfoHeader}>
                      <Ionicons name="storefront-outline" size={18} color={ticketColors.ink2} />
                      <Text style={styles.chargeInfoTitle}>Sin venta asociada</Text>
                    </View>
                    <Text style={[typography.caption, styles.chargeInfoText]}>
                      Las ventas a cuenta se cargan solas desde el POS. Esto es para un consumo que no salió de una venta.
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={[
                      posUi.secondaryButton,
                      posUi.modalActionFullWidth,
                      (saving || !canRegisterCharge) && posUi.disabledCard,
                    ]}
                    onPress={onCharge}
                    disabled={saving || !canRegisterCharge}
                    accessibilityRole="button"
                    accessibilityLabel="Registrar consumo manual"
                    testID="customers.movement.register-charge"
                  >
                    <Text style={posUi.secondaryButtonText}>
                      {saving ? 'Guardando…' : 'Registrar consumo'}
                    </Text>
                  </TouchableOpacity>
                  {chargeBlocked && amountReady ? (
                    <Text style={styles.actionHint}>{chargeBlocked.message}</Text>
                  ) : null}
                </>
              )}
            </View>
          ) : null}
          <View style={styles.movementsSectionHeader}>
            <Text style={modalExperience.formSectionTitle}>Movimientos recientes</Text>
            {previewMode && onOpenFullHistory ? (
              <TouchableOpacity onPress={onOpenFullHistory} accessibilityRole="button">
                <Text style={posUi.linkText}>Ver historial completo</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {loading ? (
            <ModalStatePanel loading loadingMessage="Cargando movimientos…" />
          ) : (
            <View style={[modalExperience.listContainer, styles.movementsListWrap]}>
            <FlatList
              data={movements}
              keyExtractor={(item) => item.id}
              style={styles.movementsList}
              onEndReached={previewMode ? undefined : onLoadMore}
              onEndReachedThreshold={0.35}
              nestedScrollEnabled
              ListEmptyComponent={
                <View style={styles.emptyMovements}>
                  <Text style={modalExperience.stateText}>Sin movimientos recientes</Text>
                </View>
              }
              ListFooterComponent={
                !previewMode && movementsLoadingMore ? (
                  <View style={modalExperience.footerLoading}>
                    <ActivityIndicator color={ticketColors.gold} size="small" />
                    <Text style={modalExperience.stateText}>Cargando más…</Text>
                  </View>
                ) : null
              }
              renderItem={({ item: movement }) => (
                <TouchableOpacity
                  style={modalExperience.listRow}
                  onPress={() => {
                    if (movement.saleId) {
                      preserveCustomerDetailForShellNavigation();
                      openSaleDetail(navigation, { saleId: movement.saleId });
                      return;
                    }
                    onOpenMovementDetail(movement);
                  }}
                >
                  <View style={styles.movementRowContent}>
                    <View style={styles.movementRowLeft}>
                      <Text style={modalExperience.listRowTitle}>
                        {getCustomerMovementDisplayLabel(movement)}
                      </Text>
                      <Text style={modalExperience.listRowTitle}>
                        {formatMoney(movement.amount)}
                      </Text>
                    </View>
                    <View style={styles.movementRowRight}>
                      <Text style={posUi.checkoutHint}>{formatLocalDate(movement.createdAt)}</Text>
                      {movement.type === 'payment' && movement.paymentMethod ? (
                        <Text style={posUi.checkoutHint}>
                          {getCustomerPaymentMethodLabel(movement.paymentMethod)}
                          {movement.check?.number ? ` N° ${movement.check.number}` : ''}
                        </Text>
                      ) : null}
                      {movement.saleId ? (
                        <Text style={posUi.checkoutHint}>
                          {movement.linkedSaleStatus === 'voided'
                            ? `Venta anulada #${movement.saleShortId || movement.saleId.slice(-6)}`
                            : `Venta POS #${movement.saleShortId || movement.saleId.slice(-6)}`}
                        </Text>
                      ) : movement.note?.startsWith('Anulación venta POS') ? (
                        <Text style={posUi.checkoutHint}>
                          Ref. #{movement.note.slice(-6)}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                </TouchableOpacity>
              )}
            />
            </View>
          )}
          </View>
          </ModalAwareScrollView>
        </View>
        </KeyboardAvoidingView>
        <ModalNestedPicker
          visible={salePickerVisible}
          title="Elegir venta para el cobro"
          options={salePickerOptions}
          selectedValue={selectedSaleId ?? ''}
          onSelect={(value) => setSelectedSaleId(value ? value : null)}
          onClose={() => setSalePickerVisible(false)}
        />
        <ModalNestedPicker
          visible={methodPickerVisible}
          title="Medio de cobro"
          options={methodPickerOptions}
          selectedValue={paymentMethod ?? ''}
          onSelect={(value) =>
            setPaymentMethod((value || null) as CustomerPaymentMethodId | null)
          }
          onClose={() => setMethodPickerVisible(false)}
        />
        {confirmOverlay ? (
          <OperationalConfirmModal visible presentation="overlay" {...confirmOverlay} />
        ) : null}
        {discardConfirmVisible ? (
          <OperationalConfirmModal
            visible
            presentation="overlay"
            title="Descartar movimiento"
            message="Tenés datos cargados. Si salís ahora los perdés."
            confirmLabel="Descartar"
            destructive
            onClose={() => setDiscardConfirmVisible(false)}
            onConfirm={() => {
              setDiscardConfirmVisible(false);
              onClose();
            }}
          />
        ) : null}
      </View>
    </Modal>
  );
}
